import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import BalanceIcon from '@mui/icons-material/Balance'
import PaymentIcon from '@mui/icons-material/Payment'
import { makeGetRequest, makePostRequest } from 'utils/api'

const MoneyBetweenUs = ({ contactId }) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [paymentRequestOpen, setPaymentRequestOpen] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMessage, setPaymentMessage] = useState('')

  useEffect(() => {
    fetchFinancialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId])

  const fetchFinancialData = async () => {
    try {
      setLoading(true)
      const response = await makeGetRequest(
        `/api/Social/contact/${contactId}/financial-operations/`,
        {},
        true
      )
      if (response.status === 200) {
        setData(response.data)
      } else {
        setError('Failed to load financial data')
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentRequest = async () => {
    try {
      const response = await makePostRequest(
        `/api/Social/contact/${contactId}/payment-request/`,
        {
          amount: paymentAmount,
          message: paymentMessage,
        },
        {},
        true
      )
      if (response.status === 200) {
        alert('Payment request sent successfully!')
        setPaymentRequestOpen(false)
        setPaymentAmount('')
        setPaymentMessage('')
      }
    } catch (err) {
      alert('Failed to send payment request: ' + err.message)
    }
  }

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'success'
    if (balance < 0) return 'error'
    return 'default'
  }

  const getBalanceIcon = (balance) => {
    if (balance > 0) return <TrendingUpIcon />
    if (balance < 0) return <TrendingDownIcon />
    return <BalanceIcon />
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No financial data available
      </Alert>
    )
  }

  const { summary, operations, contact } = data

  return (
    <Box sx={{ p: 2 }}>
      {/* Summary Section */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#1a1a2e', color: 'white' }}>
        <Typography variant="h6" gutterBottom>
          Somme avec {contact.full_name}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="grey.400">
              Lié
            </Typography>
            <Typography variant="h5">{summary.total_operations}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="grey.400">
              Total Dépenses (Je paye)
            </Typography>
            <Typography variant="h5" color="error.main">
              {summary.total_depenses.toFixed(2)} €
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="grey.400">
              Total Revenus (Il/Elle paye)
            </Typography>
            <Typography variant="h5" color="success.main">
              {summary.total_revenus.toFixed(2)} €
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="grey.400">
              Solde
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={getBalanceIcon(summary.balance)}
                label={`${summary.balance.toFixed(2)} €`}
                color={getBalanceColor(summary.balance)}
                size="medium"
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {summary.balance_message}
            </Typography>
          </Box>
        </Box>

        {summary.balance > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentRequestOpen(true)}
            >
              Demander un paiement
            </Button>
          </Box>
        )}
      </Paper>

      {/* Operations Table */}
      <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
        Historique des opérations
      </Typography>

      {operations.length === 0 ? (
        <Alert severity="info">Aucune opération trouvée avec ce contact</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Catégories</TableCell>
                <TableCell>Compte</TableCell>
                <TableCell align="right">Montant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operations.map((op) => (
                <TableRow key={op.id} hover>
                  <TableCell>{new Date(op.date).toLocaleDateString()}</TableCell>
                  <TableCell>{op.libelle}</TableCell>
                  <TableCell>
                    <Chip
                      label={op.type_operation_display}
                      color={op.type_operation === 'D' ? 'error' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{op.categories.length > 0 ? op.categories.join(', ') : '-'}</TableCell>
                  <TableCell>{op.account || '-'}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: op.type_operation === 'D' ? 'error.main' : 'success.main',
                      fontWeight: 'bold',
                    }}
                  >
                    {op.type_operation === 'D' ? '-' : '+'}
                    {op.montant.toFixed(2)} €
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payment Request Dialog */}
      <Dialog open={paymentRequestOpen} onClose={() => setPaymentRequestOpen(false)}>
        <DialogTitle>Demander un paiement à {contact.prenom}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Montant"
            type="number"
            fullWidth
            variant="outlined"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message (facultatif)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={paymentMessage}
            onChange={(e) => setPaymentMessage(e.target.value)}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Cela créera une notification de demande de paiement. Le système de notification sera
            implémenté dans les futures mises à jour.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentRequestOpen(false)}>Annuler</Button>
          <Button onClick={handlePaymentRequest} variant="contained" disabled={!paymentAmount}>
            Envoyer la demande
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MoneyBetweenUs
